/**
 * 학생 관리 — 학원 모드. 운영 학원의 학생 명단 + 추가
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoAdd, IoClose, IoPersonCircleOutline } from 'react-icons/io5';
import { useAcademy } from '../../context/AcademyContext';
import { useDialog } from '../../context/DialogContext';
import academyService from '../../services/academyService';
import BrandLoader from '../../components/common/BrandLoader';

const BizStudentsPage = () => {
  const navigate = useNavigate();
  const { operatedAcademy } = useAcademy();
  const { confirm } = useDialog();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // {id, name, phone}
  const [saving, setSaving] = useState(false);

  const aid = operatedAcademy?.id;

  const removeStudent = async (s) => {
    const ok = await confirm({
      title: '학생 삭제',
      message: `${s.name} 학생을 삭제할까요?`,
      confirmText: '삭제',
      danger: true,
    });
    if (!ok) return;
    await academyService.deleteStudent(aid, s.id);
    setStudents((p) => p.filter((x) => x.id !== s.id));
  };

  const saveEdit = async () => {
    if (!editing?.name?.trim()) return;
    setSaving(true);
    try {
      await academyService.updateStudent(aid, editing.id, {
        name: editing.name.trim(),
        phone: (editing.phone || '').trim(),
      });
      setStudents((p) => p.map((x) => (x.id === editing.id
        ? { ...x, name: editing.name.trim(), phone: (editing.phone || '').trim() }
        : x)));
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const load = useCallback(async () => {
    if (!aid) { setLoading(false); return; }
    setStudents(await academyService.getStudents(aid));
    setLoading(false);
  }, [aid]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const n = name.trim();
    if (!n || !aid) return;
    setAdding(true);
    try {
      await academyService.addStudent(aid, { name: n, phone: phone.trim() });
      setName('');
      setPhone('');
      await load();
    } catch (e) {
      console.error('학생 추가 실패:', e);
    } finally {
      setAdding(false);
    }
  };

  if (!operatedAcademy) {
    return (
      <Container>
        <Empty>
          <EmptyText>운영 중인 학원이 없어요.</EmptyText>
          <EmptyBtn type="button" onClick={() => navigate('/academy/register')}>학원 신청하기</EmptyBtn>
        </Empty>
      </Container>
    );
  }
  if (loading) return <BrandLoader />;

  return (
    <Container>
      <Title>학생 관리</Title>
      <Sub>{operatedAcademy.name} · 학생 {students.length}명</Sub>

      <AddLabel>학생 추가 — 이름 / 부모님 전화번호 (출석 모드용)</AddLabel>
      <AddRow>
        <AddInput
          placeholder="이름"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <AddInput
          placeholder="부모님 전화번호 (예: 01012345678)"
          inputMode="numeric"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
        />
        <AddBtn type="button" disabled={adding || !name.trim()} onClick={add}>
          <IoAdd size={18} /> 추가
        </AddBtn>
      </AddRow>

      {students.length === 0 ? (
        <Hint>아직 등록된 학생이 없어요.</Hint>
      ) : (
        <StuGrid>
          {students.map((s) => (
            <StuCard
              key={s.id}
              type="button"
              onClick={() => setEditing({
                id: s.id, name: s.name, phone: s.phone || '', age: s.age ?? '',
              })}
            >
              <Avatar $photo={s.photoUrl} />
              <StuMain>
                <StuTopRow>
                  <StuName>{s.name}</StuName>
                  {s.age != null && s.age !== '' && <StuAge>{s.age}세</StuAge>}
                </StuTopRow>
                {s.phone && <StuPhone>{s.phone}</StuPhone>}
              </StuMain>
            </StuCard>
          ))}
        </StuGrid>
      )}

      {editing && (
        <Overlay onClick={() => setEditing(null)}>
          <Sheet onClick={(e) => e.stopPropagation()}>
            <SheetHead>
              <SheetTitle>학생 정보 수정</SheetTitle>
              <SheetClose type="button" onClick={() => setEditing(null)}>
                <IoClose size={22} />
              </SheetClose>
            </SheetHead>
            <FLabel>이름</FLabel>
            <AddInput
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              autoFocus
            />
            <FLabel>부모님 전화번호</FLabel>
            <AddInput
              placeholder="01012345678"
              inputMode="numeric"
              value={editing.phone}
              onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
            />
            <ModalActions>
              <DeleteBtn
                type="button"
                onClick={async () => {
                  const target = students.find((x) => x.id === editing.id);
                  if (!target) return;
                  setEditing(null);
                  await removeStudent(target);
                }}
              >
                삭제
              </DeleteBtn>
              <AddBtn type="button" disabled={saving || !editing.name.trim()} onClick={saveEdit}>
                {saving ? '저장 중...' : '저장'}
              </AddBtn>
            </ModalActions>
          </Sheet>
        </Overlay>
      )}
    </Container>
  );
};

export default BizStudentsPage;

const Container = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 22px 16px;
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Sub = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 4px;
`;

const AddLabel = styled.label`
  display: block;
  font-size: 13.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 20px 0 8px;
`;

const AddRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const AddInput = styled.input`
  width: 100%;
  padding: 13px 14px;
  border: 0;
  border-radius: 11px;
  background: #F4F5F7;
  font-size: 15px;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 13px 16px;
  border-radius: 11px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  &:disabled { background: #D7D9E0; }
`;

/* ── 학생 카드 2열 그리드 (출석 탭과 동일 톤) ───── */
const StuGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 9px;
`;

const StuCard = styled.button`
  display: flex;
  gap: 10px;
  padding: 11px;
  border-radius: 13px;
  text-align: left;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  &:active { opacity: 0.7; }
`;

const AvatarBase = styled.div`
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $photo }) => ($photo ? `#fff url(${$photo}) center / cover no-repeat` : '#F0F1F4')};
  color: #9CA3AF;
`;

const Avatar = ({ $photo }) => (
  <AvatarBase $photo={$photo}>
    {!$photo && <IoPersonCircleOutline size={42} />}
  </AvatarBase>
);

const StuMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const StuTopRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const StuName = styled.span`
  font-size: 14.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StuAge = styled.span`
  font-size: 11.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.hint};
  flex-shrink: 0;
`;

const StuPhone = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeleteBtn = styled.button`
  flex: 0 0 auto;
  padding: 13px 18px;
  border-radius: 11px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: ${({ theme }) => theme.colors.accent.red};
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 18px 18px 24px;
`;

const SheetHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const SheetTitle = styled.p`
  font-size: 17px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SheetClose = styled.button`
  background: none;
  display: flex;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const FLabel = styled.p`
  margin: 10px 0 6px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  /* AddBtn은 내용폭이 기본 — 저장 버튼을 큰 비율로 */
  & > button:last-child { flex: 1; }
`;

const Hint = styled.p`
  text-align: center;
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 40px 0;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 100px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const EmptyBtn = styled.button`
  padding: 12px 26px;
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
`;
